import React, { useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useDispatch, useSelector } from "react-redux";
import { getEventInsightMetric } from "../../../store/metric/actions";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { NextPlanOutlined } from "@mui/icons-material";
import ApiLoading from "../components/ApiLoading";

const EventInsight = () => {
  const dispatch = useDispatch();
  const organizations = useOrganizations();
  const orgCode = organizations?.[0]?.orgcode;

  const { eventInsightMetric, eventInsightMetricLoading } = useSelector(
    (state) => ({
      eventInsightMetric: state.metricInfo.eventInsightMetric,
      eventInsightMetricLoading: state.metricInfo.eventInsightMetricLoading,
    })
  );

  useEffect(() => {
    if (orgCode) {
      dispatch(getEventInsightMetric(orgCode));
    }
  }, [dispatch, orgCode]);

  const data =
    eventInsightMetric && typeof eventInsightMetric === "object"
      ? eventInsightMetric
      : {};
  const metrics = Object.entries(data).filter(
    ([, value]) =>
      value === null || ["string", "number", "boolean"].includes(typeof value)
  );

  const handleGoTo = () => {
    window.location.href = "/cloudevent?tab=immediate-actions";
  };

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        padding: "8px 12px",
        height: { xs: 340, sm: 320 },
        display: "flex",
        flexDirection: "column",
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ mb: 1, paddingBottom: "2px", borderBottom: `1px solid` }}
      >
        <Box display="flex" alignItems="center" minWidth={0}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Risks
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Open Detailed View">
            <IconButton
              aria-label="go-to"
              sx={{ color: "text.secondary", fontSize: "20px", transform: "rotate(-45deg)" }}
              onClick={handleGoTo}
            >
              <NextPlanOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ alignSelf: "center", height: "18px" }} />
          <IconButton
            aria-label="settings"
            className="drag-handle"
            sx={{ color: "text.secondary", fontSize: "20px" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      {eventInsightMetricLoading ? (
        <ApiLoading variant="card" height={150} width="100%" />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            width: "100%",
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "grey.400",
                borderRadius: "10px",
              },
              scrollbarWidth: "thin",
            }}
          >
            {metrics.map(([label, value], index) => (
              <React.Fragment key={label}>
                {index > 0 && <Divider sx={{ flexShrink: 0 }} />}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="body2">{label}</Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    {value === null ? "null" : String(value)}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              mt: 0.5,
              pt: 1,
              pb: 0.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                letterSpacing: 0.2,
                lineHeight: 1.2,
              }}
            >
              Last 24 hours
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EventInsight;
